import { useState, useCallback, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ChevronLeft, ChevronRight, Search, Filter, X } from 'lucide-react'
import { usePackages, PackageFilters } from '../hooks/usePackages'
import { useFranchiseData } from '../hooks/useFranchiseData'
import { usePackageTypes } from '../hooks/usePackageTypes'
import { useDestinations } from '../hooks/useDestinations'
import SEO from '../components/SEO'
import { buildCollectionPageSchema } from '../utils/schema'
import PackageCard, { PackageCardSkeleton } from '../components/PackageCard'
import FlexPackages from '../components/FlexPackages'

// const categories = ['All', 'Luxury', 'Golf', 'Beach', 'City Break', 'Adults Only', 'Eco-Friendly']

export default function Packages() {
  // Read URL parameters
  const queryParams = new URLSearchParams(window.location.search)
  const initialPage = parseInt(queryParams.get('page') || '1', 10)
  const urlQuery = queryParams.get('query') || ''
  const urlDestination = queryParams.get('destination') || ''
  // const urlFranchiseId = queryParams.get('franchise_id') || '' // Not needed as franchise_id is handled by API service
  
  const [selectedCategory, setSelectedCategory] = useState('All')
  const [searchTerm, setSearchTerm] = useState(urlQuery)
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)
  
  // Advanced filter states
  const [selectedDestination, setSelectedDestination] = useState(urlDestination)
  const [selectedPackageType, setSelectedPackageType] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [priceRange, setPriceRange] = useState('')

  // Build initial filters from URL parameters
  const buildInitialFilters = (): PackageFilters => {
    const filters: PackageFilters = {}
    
    if (urlQuery) {
      filters.query = urlQuery
    }

    if (urlDestination) {
      filters.destination = parseInt(urlDestination, 10)
    }
    
    return filters
  }

  const syncFiltersToUrl = (filters: PackageFilters, page?: number) => {
    const params = new URLSearchParams()
    if (filters.query) params.set('query', String(filters.query))
    if (filters.destination) params.set('destination', String(filters.destination))
    if (page && page > 1) params.set('page', page.toString())
    const search = params.toString()
    window.history.pushState({}, '', search ? `/packages?${search}` : '/packages')
  }

  // Function to update URL with new page while preserving other parameters
  const updateUrlPage = (page: number) => {
    const currentParams = new URLSearchParams(window.location.search)
    currentParams.set('page', page.toString())
    window.history.pushState({}, '', `/packages?${currentParams.toString()}`)
  }
  
  const { loading: franchiseLoading } = useFranchiseData()
  const { packageTypes } = usePackageTypes()
  const { destinations } = useDestinations()
  const { 
    packages, 
    pagination, 
    loading: packagesLoading, 
    error,
    applyFilters,
    loadPage 
  } = usePackages({ 
    initialPage,
    initialFilters: buildInitialFilters()
  })

  const loading = franchiseLoading || packagesLoading

  // Override pagination functions to update URL
  const goToNextPageWithUrl = () => {
    if (pagination.has_next) {
      const nextPage = pagination.current_page + 1
      loadPage(nextPage)
      updateUrlPage(nextPage)
    }
  }

  const goToPrevPageWithUrl = () => {
    if (pagination.has_prev) {
      const prevPage = pagination.current_page - 1
      loadPage(prevPage)
      updateUrlPage(prevPage)
    }
  }

  const loadPageWithUrl = (page: number) => {
    loadPage(page)
    updateUrlPage(page)
  }

  const buildFilters = (overrides?: Partial<PackageFilters>): PackageFilters => {
    const filters: PackageFilters = {}
    
    // Use overrides if provided, otherwise use state values
    const category = overrides?.category !== undefined ? overrides.category : selectedCategory
    const search = overrides?.search !== undefined ? overrides.search : searchTerm
    const query = overrides?.query !== undefined ? overrides.query : (urlQuery || searchTerm)
    const destination = overrides?.destination !== undefined ? overrides.destination : selectedDestination
    const offerType = overrides?.offer_type !== undefined ? overrides.offer_type : selectedPackageType
    const dateFromValue = overrides?.date_from !== undefined ? overrides.date_from : dateFrom
    const dateToValue = overrides?.date_to !== undefined ? overrides.date_to : dateTo
    const price = overrides?.price !== undefined ? overrides.price : priceRange
    
    if (category && category !== 'All') {
      filters.category = category
    }
    
    // Prioritize query parameter over search
    if (query && query.trim()) {
      filters.query = query.trim()
    } else if (search && search.trim()) {
      filters.search = search.trim()
    }
    
    if (destination) {
      filters.destination = typeof destination === 'string' ? parseInt(destination) : destination
    }
    
    if (offerType) {
      filters.offer_type = typeof offerType === 'string' ? parseInt(offerType) : offerType
    }
    
    if (dateFromValue) {
      filters.date_from = dateFromValue
    }
    
    if (dateToValue) {
      filters.date_to = dateToValue
    }
    
    if (price) {
      filters.price = price
    }
    
    return filters
  }

  // const handleCategoryChange = (category: string) => {
  //   setSelectedCategory(category)
  //   const filters = { ...buildFilters(), category: category !== 'All' ? category : undefined }
  //   applyFilters(filters)
  // }

  const handleSearch = useCallback((e?: React.FormEvent) => {
    if (e) e.preventDefault()
    const filters = buildFilters()
    applyFilters(filters)
    syncFiltersToUrl(filters)
  }, [searchTerm, urlQuery, selectedCategory, selectedDestination, selectedPackageType, dateFrom, dateTo, priceRange])

  const handleSearchInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = e.target.value
    setSearchTerm(newSearchTerm)
    
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    
    // Set a new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      const filters = {
        ...buildFilters(),
        query: newSearchTerm || undefined,
        search: undefined // Clear search when using query
      }
      applyFilters(filters)
    }, 500) // 500ms debounce
  }, [urlQuery, selectedCategory, selectedDestination, selectedPackageType, dateFrom, dateTo, priceRange])

  const searchTimeoutRef = useRef<number | null>(null)

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
    }
  }, [])

  const handleAdvancedFilterApply = () => {
    const filters = buildFilters()
    applyFilters(filters)
    syncFiltersToUrl(filters)
    setShowAdvancedFilters(false)
  }

  const clearAllFilters = useCallback(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    
    setSelectedCategory('All')
    setSearchTerm('')
    setSelectedDestination('')
    setSelectedPackageType('')
    setDateFrom('')
    setDateTo('')
    setPriceRange('')
    applyFilters({})
    window.history.pushState({}, '', '/packages')
  }, [])

  const hasActiveFilters = selectedCategory !== 'All' || searchTerm || selectedDestination || selectedPackageType || dateFrom || dateTo || priceRange
  const destinationName = destinations.find((d) => d.id.toString() === selectedDestination)?.name
  const pageTitle = destinationName ? `${destinationName} Packages` : 'Featured Escapes'

  const filterInputClass =
    'w-full px-3 py-2 border border-line rounded-[10px] bg-paper text-ink focus:outline-none focus:ring-2 focus:ring-vaia-amber focus:border-vaia-amber'

  if (loading && !packages.length) {
    return (
      <div className="bg-cream">
        <div className="page-section">
          <SEO
            title={pageTitle}
            description="Browse curated travel packages to top destinations."
            canonical={typeof window !== 'undefined' ? window.location.href : undefined}
          />
          <div className="sec-head">
            <h2>{pageTitle}</h2>
          </div>
          <div className="cards">
            {[...Array(6)].map((_, i) => (
              <PackageCardSkeleton key={i} />
            ))}
          </div>
          <FlexPackages />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-cream">
        <div className="page-section">
          <SEO title="Packages Error" noindex />
          <div className="text-center">
            <h2 className="text-3xl">Something went wrong</h2>
            <p className="mt-6 text-lg text-vaia-amber-deep">{error}</p>
            <button onClick={() => window.location.reload()} className="mt-8 btn-primary">
              Try Again
            </button>
          </div>
          <FlexPackages />
        </div>
      </div>
    )
  }

  return (
    <div className="bg-cream">
      <div className="page-section">
        <SEO
          title={pageTitle}
          description="Browse curated travel packages — flights, stays and visa guidance included."
          canonical={typeof window !== 'undefined' ? window.location.href : undefined}
          openGraph={{
            type: 'website',
            title: pageTitle,
            description: 'Browse curated travel packages — flights, stays and visa guidance included.',
            url: typeof window !== 'undefined' ? window.location.href : undefined,
          }}
          twitter={{ card: 'summary_large_image' }}
          jsonLd={buildCollectionPageSchema({
            name: pageTitle,
            description: 'Browse curated travel packages — flights, stays and visa guidance included.',
            url: typeof window !== 'undefined' ? window.location.href : ''
          })}
        />
        <div className="eyebrow">{destinationName ? 'Destination' : 'Packages'}</div>
        <div className="sec-head">
          <h2>{pageTitle}</h2>
        </div>

        {/* Search and Filter Bar */}
        <div className="mx-auto mb-10 max-w-4xl">
          <form onSubmit={handleSearch} className="flex flex-col gap-4">
            <div className="flex gap-2 flex-wrap sm:flex-nowrap">
              <div className="flex-1 relative min-w-0 w-full">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-ink-soft" />
                <input
                  type="text"
                  placeholder="Search packages..."
                  value={searchTerm}
                  onChange={handleSearchInputChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      if (searchTimeoutRef.current) {
                        clearTimeout(searchTimeoutRef.current)
                      }
                      handleSearch()
                    }
                  }}
                  className="w-full pl-10 pr-4 py-2.5 border border-line rounded-[10px] bg-paper focus:outline-none focus:ring-2 focus:ring-vaia-amber"
                />
              </div>
              <button 
                type="button"
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className={`flex items-center gap-2 px-4 py-2.5 border rounded-[10px] text-sm font-semibold transition-colors ${
                  showAdvancedFilters 
                    ? 'border-forest bg-forest text-paper' 
                    : 'border-line text-forest hover:bg-paper'
                }`}
              >
                <Filter className="h-4 w-4" />
                Filters
              </button>
              <button type="submit" className="btn-primary !py-2.5">
                Search
              </button>
            </div>

            {/* Advanced Filters Panel */}
            {showAdvancedFilters && (
              <div className="p-6 border border-line rounded-[14px] bg-paper">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-forest">Advanced Filters</h3>
                  <button
                    type="button"
                    onClick={() => setShowAdvancedFilters(false)}
                    className="text-ink-soft hover:text-forest"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Destination Filter */}
                  <div>
                    <label className="block text-sm font-medium text-forest mb-2">
                      Destination
                    </label>
                    <select
                      value={selectedDestination}
                      onChange={(e) => setSelectedDestination(e.target.value)}
                      className={filterInputClass}
                    >
                      <option value="">All Destinations</option>
                      {destinations.map((destination) => (
                        <option key={destination.id} value={destination.id}>
                          {destination.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Package Type Filter */}
                  <div>
                    <label className="block text-sm font-medium text-forest mb-2">
                      Package Type
                    </label>
                    <select
                      value={selectedPackageType}
                      onChange={(e) => setSelectedPackageType(e.target.value)}
                      className={filterInputClass}
                    >
                      <option value="">All Types</option>
                      {packageTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Date From */}
                  <div>
                    <label className="block text-sm font-medium text-forest mb-2">
                      Date From
                    </label>
                    <input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className={filterInputClass}
                    />
                  </div>

                  {/* Date To */}
                  <div>
                    <label className="block text-sm font-medium text-forest mb-2">
                      Date To
                    </label>
                    <input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className={filterInputClass}
                    />
                  </div>

                  {/* Price Range */}
                  <div>
                    <label className="block text-sm font-medium text-forest mb-2">
                      Price Range
                    </label>
                    <select
                      value={priceRange}
                      onChange={(e) => setPriceRange(e.target.value)}
                      className={filterInputClass}
                    >
                      <option value="">Any Price</option>
                      <option value="0">Under R5000</option>
                      <option value="5-10">R5000 - R10000</option>
                      <option value="10-20">R10000 - R20000</option>
                      <option value="20">R20000 Upwards</option>
                    </select>
                  </div>
                </div>

                <div className="flex justify-between mt-6">
                  <button
                    type="button"
                    onClick={clearAllFilters}
                    className="px-4 py-2 text-sm text-ink-soft hover:text-forest"
                  >
                    Clear All Filters
                  </button>
                  <button
                    type="button"
                    onClick={handleAdvancedFilterApply}
                    className="btn-primary"
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Categories Filter
        <div className="mx-auto mt-8 flex flex-wrap justify-center gap-4">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => handleCategoryChange(category)}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                selectedCategory === category
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-900 hover:bg-purple-100 hover:text-purple-700'
              }`}
            >
              {category}
            </button>
          ))}
        </div> */}

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="mx-auto mt-6 max-w-4xl">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-ink-soft">Active filters:</span>
              {selectedCategory !== 'All' && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-forest text-paper text-sm">
                  Category: {selectedCategory}
                  <X className="h-3 w-3 cursor-pointer" onClick={() => setSelectedCategory('All')} />
                </span>
              )}
              {selectedDestination && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-paper border border-line text-forest text-sm">
                  Destination: {destinationName || selectedDestination}
                  <X
                    className="h-3 w-3 cursor-pointer"
                    onClick={() => {
                      setSelectedDestination('')
                      const filters = buildFilters({ destination: '' })
                      applyFilters(filters)
                      syncFiltersToUrl(filters)
                    }}
                  />
                </span>
              )}
              {selectedPackageType && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-paper border border-line text-forest text-sm">
                  Type: {packageTypes.find(t => t.id.toString() === selectedPackageType)?.name}
                  <X className="h-3 w-3 cursor-pointer" onClick={() => setSelectedPackageType('')} />
                </span>
              )}
              {(dateFrom || dateTo) && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-paper border border-line text-forest text-sm">
                  Dates: {dateFrom} - {dateTo}
                  <X className="h-3 w-3 cursor-pointer" onClick={() => { setDateFrom(''); setDateTo('') }} />
                </span>
              )}
              {priceRange && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#FBF1DF] text-vaia-amber-deep text-sm">
                  Price: {
                    priceRange === '0' ? 'Under R5000' :
                    priceRange === '5-10' ? 'R5000 - R10000' :
                    priceRange === '10-20' ? 'R10000 - R20000' :
                    priceRange === '20' ? 'R20000 Upwards' : ''
                  }
                  <X className="h-3 w-3 cursor-pointer" onClick={() => setPriceRange('')} />
                </span>
              )}
              <button
                onClick={clearAllFilters}
                className="text-sm text-ink-soft hover:text-forest underline"
              >
                Clear all
              </button>
            </div>
          </div>
        )}

        <div className="mx-auto mt-8 mb-8 text-center text-sm text-ink-soft">
          {destinationName ? (
            `Showing ${packages.length} of ${pagination.total_count} packages in ${destinationName}`
          ) : (urlQuery || searchTerm) ? (
            `Found ${packages.length} packages matching "${urlQuery || searchTerm}"`
          ) : (
            `Showing ${packages.length} of ${pagination.total_count} packages${selectedCategory !== 'All' ? ` in "${selectedCategory}"` : ''}`
          )}
        </div>

        <div className="cards">
          {packages.map((pkg, index) => (
            <PackageCard key={pkg.id} pkg={pkg} index={index} />
          ))}
        </div>

        {packages.length === 0 && !loading && (
          <div className="mx-auto mt-16 max-w-2xl text-center">
            <h3 className="text-lg font-semibold text-forest">No packages found</h3>
            <p className="mt-2 text-sm text-ink-soft">
              Try adjusting your search or filter criteria.
            </p>
            <button 
              onClick={clearAllFilters}
              className="mt-4 btn-ghost"
            >
              Clear all filters
            </button>
          </div>
        )}

        {pagination.total_pages > 1 && !urlQuery && !searchTerm && (
          <div className="mx-auto mt-16 flex items-center justify-center gap-4">
            <button
              onClick={goToPrevPageWithUrl}
              disabled={!pagination.has_prev}
              className="flex items-center px-4 py-2 text-sm font-medium text-forest bg-paper border border-line rounded-[10px] hover:bg-cream disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </button>
            
            <div className="flex items-center gap-2">
              {Array.from({ length: Math.min(pagination.total_pages, 7) }, (_, i) => {
                let page
                if (pagination.total_pages <= 7) {
                  page = i + 1
                } else {
                  const current = pagination.current_page
                  if (current <= 4) {
                    page = i + 1
                  } else if (current >= pagination.total_pages - 3) {
                    page = pagination.total_pages - 6 + i
                  } else {
                    page = current - 3 + i
                  }
                }
                
                return (
                  <button
                    key={page}
                    onClick={() => loadPageWithUrl(page)}
                    className={`px-3 py-2 text-sm font-medium rounded-[10px] ${
                      page === pagination.current_page
                        ? 'bg-forest text-paper'
                        : 'text-ink-soft hover:bg-paper'
                    }`}
                  >
                    {page}
                  </button>
                )
              })}
            </div>

            <button
              onClick={goToNextPageWithUrl}
              disabled={!pagination.has_next}
              className="flex items-center px-4 py-2 text-sm font-medium text-forest bg-paper border border-line rounded-[10px] hover:bg-cream disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </button>
          </div>
        )}

        {loading && packages.length > 0 && (
          <div className="mx-auto mt-16 text-center">
            <div className="inline-flex items-center px-4 py-2 text-sm font-medium text-ink-soft">
              <div className="animate-spin h-4 w-4 border-2 border-forest border-t-transparent rounded-full mr-2"></div>
              Loading...
            </div>
          </div>
        )}

        <FlexPackages />

        <div className="visa-teaser mt-16">
          <div>
            <h2>Can't find what you're looking for?</h2>
            <p>A Lifestyle Planner can design a custom trip — flights, stays and visa guidance included.</p>
          </div>
          <Link to="/contact" className="btn-primary">Plan My Trip</Link>
        </div>
      </div>
    </div>
  )
} 